import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import {
  buttonSecondary,
  SectionHeading,
  tone,
  useOpenAgentDemo,
} from './primitives';

type Faq = { question: string; answer: string };

/** The page's questions; industry pages pass their own. */
export default function AgentFaq({ faqs: pageFaqs }: { faqs?: Faq[] }) {
  const { t } = useLocale('chatbots');
  const openDemo = useOpenAgentDemo();

  const faqs: Faq[] = pageFaqs ?? [
    {
      question: t('What is an AI agent builder?'),
      answer: t(
        'A tool for making your own AI agent without writing code. You give it your website, files and instructions, choose what it can do, such as booking meetings or checking orders, and put it on your website, WhatsApp, email or a phone line. Easy-Peasy.AI does all of that in one place.',
      ),
    },
    {
      question: t('What is the difference between an AI agent and a chatbot?'),
      answer: t(
        'A traditional chatbot follows a fixed script of buttons and set replies. An AI agent understands questions in everyday language, answers from your own content and can take actions, like booking a meeting, collecting a lead or checking an order. Many people still call both a chatbot.',
      ),
    },
    {
      question: t('Do I need to know how to code?'),
      answer: t(
        'No. Add your website or files, write instructions in plain language and paste one line of code to put the chat on your site. You can be live in minutes.',
      ),
    },
    {
      question: t('What can my agent learn from?'),
      answer: t(
        'Websites and sitemaps, PDF, Word, CSV, TXT and EPUB files, YouTube videos, plain text and Q&A pairs. You can retrain it with one click whenever something changes.',
      ),
    },
    {
      question: t('Where can customers talk to it?'),
      answer: t(
        'On your website as a chat bubble or an embedded page, through a shareable link, on WhatsApp, Messenger, Telegram, LINE and Slack, by email through Gmail, Outlook or email forwarding, and over the phone with a Twilio number. Shopify, WordPress, Zapier and the REST API are supported too.',
      ),
    },
    {
      question: t('Can it answer phone calls?'),
      answer: t(
        'Yes. Connect a Twilio phone number and customers can call your agent. On Unlimited 200 and up, visitors can also talk to it by voice in your chat widget, and you can try voice for free in the Playground.',
      ),
    },
    {
      question: t('What happens when it doesn’t know the answer?'),
      answer: t(
        'It answers from your knowledge and follows your instructions, so you decide what it says when it isn’t sure, for example offering to take an email address. You can review every conversation and correct an answer in one click.',
      ),
    },
    {
      question: t('Which AI models can I use?'),
      answer: t(
        'Models from OpenAI, Anthropic, Google, DeepSeek, xAI and more. You can switch at any time without retraining.',
      ),
    },
    {
      question: t('Is there a free plan?'),
      answer: t(
        'Yes. The Free plan includes one agent and 100 messages a month on your website, Telegram, Messenger and Slack. No credit card needed.',
      ),
    },
    {
      question: t('Can I remove the Easy-Peasy.AI branding?'),
      answer: t(
        'Yes. On Unlimited 200 and up you can hide the “Powered by” badge, and on every plan you can use your own colors, avatar and name.',
      ),
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title={t('Questions, answered.')}
            lead={t(
              'Still wondering about something? Ask the agent on this page. We built it with the same tools you get.',
            )}
          />
          <button
            type='button'
            onClick={openDemo}
            className={clsxm(buttonSecondary, 'mt-8')}
          >
            <span className='relative flex h-2 w-2'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
              <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
            </span>
            {t('Talk to our agent')}
          </button>
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }) => (
              <li key={question}>
                <details className='group'>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    {question}
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
